import { useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/shared/utils/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  'aria-label'?: string;
}

const firstEnabledIndex = (options: SelectOption[]) => options.findIndex((o) => !o.disabled);

const lastEnabledIndex = (options: SelectOption[]) => {
  for (let i = options.length - 1; i >= 0; i -= 1) {
    if (!options[i].disabled) return i;
  }
  return -1;
};

const nextEnabledIndex = (options: SelectOption[], from: number, direction: 1 | -1) => {
  let i = from;
  for (let step = 0; step < options.length; step += 1) {
    i += direction;
    if (i < 0 || i >= options.length) return from;
    if (!options[i].disabled) return i;
  }
  return from;
};

export const Select = ({
  value,
  onChange,
  options,
  id,
  disabled,
  placeholder = 'Select…',
  className,
  'aria-label': ariaLabel,
}: SelectProps) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const generatedId = useId();
  const triggerId = id ?? generatedId;
  const listboxId = `${generatedId}-listbox`;
  const optionId = (index: number) => `${listboxId}-option-${index}`;

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const activeItem = listRef.current?.querySelector<HTMLLIElement>(`#${CSS.escape(optionId(activeIndex))}`);
    activeItem?.scrollIntoView?.({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeIndex]);

  const openList = (initialIndex: number) => {
    setActiveIndex(initialIndex);
    setOpen(true);
  };

  const commit = (index: number) => {
    const option = options[index];
    if (!option || option.disabled) return;
    if (option.value === value) {
      setOpen(false);
      return;
    }
    onChange(option.value);
    setOpen(false);
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' ', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        let initialIndex: number;
        if (event.key === 'Home') {
          initialIndex = firstEnabledIndex(options);
        } else if (event.key === 'End') {
          initialIndex = lastEnabledIndex(options);
        } else {
          initialIndex = selectedIndex >= 0 ? selectedIndex : firstEnabledIndex(options);
        }
        openList(initialIndex);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((current) => nextEnabledIndex(options, current, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((current) => nextEnabledIndex(options, current, -1));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(firstEnabledIndex(options));
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(lastEnabledIndex(options));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        commit(activeIndex);
        break;
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    if (open) {
      setOpen(false);
    } else {
      openList(selectedIndex >= 0 ? selectedIndex : firstEnabledIndex(options));
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        id={triggerId}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        className={cn('input flex items-center justify-between text-left', className)}
      >
        <span className={cn('truncate', !selectedOption && 'text-muted-foreground')}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          id={listboxId}
          tabIndex={-1}
          aria-labelledby={triggerId}
          className="surface-overlay absolute top-full z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border p-1 shadow"
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              role="option"
              id={optionId(index)}
              aria-selected={index === selectedIndex}
              aria-disabled={option.disabled}
              onClick={() => commit(index)}
              onMouseMove={() => setActiveIndex(index)}
              onMouseEnter={() => setActiveIndex(index)}
              className={cn(
                'flex cursor-pointer items-center justify-between gap-2 rounded-sm border border-transparent px-2 py-1.5 text-sm text-foreground',
                'hover:bg-muted',
                index === activeIndex && 'bg-muted',
                index === selectedIndex && 'border-primary bg-primary/10',
                option.disabled && 'pointer-events-none cursor-not-allowed opacity-50'
              )}
            >
              <span className="truncate">{option.label}</span>
              {index === selectedIndex && <Check className="h-4 w-4 shrink-0 text-primary" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
